import { useEffect, useState, useCallback } from "react";
import { ShieldCheck, Trash2, UserPlus, Users } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";

const inputCls = "bg-[#0D131E] border-[#243046] font-mono text-slate-100 text-sm focus-visible:ring-amber-500";

export const AdminPanel = ({ open, onOpenChange, currentUserId }) => {
  const [users, setUsers] = useState([]);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load users");
    }
  }, []);

  useEffect(() => {
    if (open) refresh();
  }, [open, refresh]);

  const createUser = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/users", { email: email.trim(), name: name.trim(), password });
      setEmail("");
      setName("");
      setPassword("");
      await refresh();
      toast.success("User account created");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not create user");
    } finally {
      setBusy(false);
    }
  };

  const deleteUser = async (id) => {
    try {
      await api.delete(`/admin/users/${id}`);
      await refresh();
      toast.success("User deleted");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#131924] border-[#243046] text-slate-200 max-w-lg" data-testid="admin-panel-dialog">
        <DialogHeader>
          <DialogTitle className="font-mono text-slate-100 flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-amber-400" /> Manage Users
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-xs font-mono">Create and remove user accounts. Each user&rsquo;s saved tanks are private.</DialogDescription>
        </DialogHeader>

        <form onSubmit={createUser} className="space-y-2 border border-[#243046] rounded-lg p-3" data-testid="create-user-form">
          <div className="grid grid-cols-2 gap-2">
            <Input data-testid="new-user-email" type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
            <Input data-testid="new-user-name" placeholder="Name (optional)" value={name} onChange={(e) => setName(e.target.value)} className={inputCls} />
          </div>
          <div className="flex gap-2">
            <Input data-testid="new-user-password" type="text" required minLength={6} placeholder="Password (min 6 chars)" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} />
            <Button data-testid="create-user-btn" type="submit" disabled={busy} className="shrink-0 bg-amber-500 text-[#0B0F17] hover:bg-amber-400 font-semibold">
              <UserPlus />Add
            </Button>
          </div>
        </form>

        <div className="flex items-center gap-2 text-xs font-mono text-slate-400">
          <Users className="h-3.5 w-3.5" /> {users.length} user{users.length === 1 ? "" : "s"}
        </div>
        <ul className="space-y-1.5 max-h-64 overflow-y-auto" data-testid="users-list">
          {users.map((u, i) => (
            <li key={u.id} data-testid={`user-row-${i}`} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-[#243046] bg-[#0D131E]">
              <span className="flex-1 min-w-0">
                <span className="block text-xs font-mono text-slate-200 truncate">{u.name || u.email}</span>
                <span className="block text-[10px] font-mono text-slate-500 truncate">{u.email}</span>
              </span>
              <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full ${u.role === "admin" ? "bg-amber-500/15 text-amber-300 border border-amber-500/30" : "bg-slate-700/40 text-slate-400"}`}>{u.role}</span>
              {u.id !== currentUserId && (
                <button title="Delete user" data-testid={`delete-user-btn-${i}`} onClick={() => deleteUser(u.id)} className="p-1.5 rounded text-slate-400 hover:text-red-400 hover:bg-[#1A2232]">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </li>
          ))}
        </ul>
      </DialogContent>
    </Dialog>
  );
};
